import type {
  BalanceRegistration,
  BalanceSlot,
  BalanceSlotInvitee,
} from '@sportninja/common/services/teamBalance'
import { formatInViewerZone } from '@sportninja/common/ts-utils/date-utils'
import type { FinderHiddenCause } from './balanceFinder'
import { finderHiddenCause, isSlotListedButHidden } from './balanceFinder'
import { balanceText } from './balanceI18n'
import { inviteeName, slotType } from './balanceRowKind'

/** Why a listed slot's ad is hidden, and what brings it back (review round 1, TF-03). */
export const HIDDEN_CAUSE_TIP_KEYS: Record<
  FinderHiddenCause,
  { tip: string; next: string }
> = {
  finder_disabled: {
    tip: 'common:balanceStatusTipPlayerFinderHiddenFinderDisabled',
    next: 'common:balanceStatusNextPlayerFinderHiddenFinderDisabled',
  },
  registration_private: {
    tip: 'common:balanceStatusTipPlayerFinderHiddenPrivate',
    next: 'common:balanceStatusNextPlayerFinderHiddenPrivate',
  },
  recruiting_off: {
    tip: 'common:balanceStatusTipPlayerFinderHidden',
    next: 'common:balanceStatusNextPlayerFinderHidden',
  },
  roster_full: {
    tip: 'common:balanceStatusTipPlayerFinderHiddenRosterFull',
    next: 'common:balanceStatusNextPlayerFinderHiddenRosterFull',
  },
  other: {
    tip: 'common:balanceStatusTipPlayerFinderHiddenOther',
    next: 'common:balanceStatusNextPlayerFinderHiddenOther',
  },
}

/**
 * What the name column says for a player slot nobody is in yet:
 *
 * - `invitee` — the person it is held for, by name alone. When the invite went
 *   out and to whom is the STATUS tooltip's first line (`inviteSentFact`);
 * - `player_finder` — a Player Finder Slot, with why its ad is hidden when players
 *   cannot see it (null while it is advertised);
 * - `player` — a plain Open Player Slot.
 */
export type UnfilledSlotDisplay =
  | { kind: 'invitee'; name: string }
  | {
      kind: 'player_finder'
      hidden: { tip: string; next: string } | null
    }
  | { kind: 'player' }

/**
 * Jeff, Oct 1 — an unfilled slot's TYPE, said in the name column so the STATUS
 * column carries only the lifecycle. The invitee's line never says "Invited":
 * STATUS already does.
 */
export const unfilledSlotDisplay = (
  slot: BalanceSlot,
  registration: Pick<
    BalanceRegistration,
    'player_finder_hidden_reasons' | 'player_finder_recruiting'
  >
): UnfilledSlotDisplay => {
  const type = slotType(slot)

  if (type === 'invited' && slot.invitee) {
    return { kind: 'invitee', name: inviteeName(slot.invitee) }
  }

  if (type === 'player_finder') {
    return {
      kind: 'player_finder',
      hidden: isSlotListedButHidden(slot)
        ? HIDDEN_CAUSE_TIP_KEYS[finderHiddenCause(registration)]
        : null,
    }
  }

  return { kind: 'player' }
}

/**
 * Jeff, Oct 1 — where an imported invitee came from, the second line of the
 * Invited tooltip's fact box: "From {season}". Null on a hold the rep typed, and
 * from an older api2 that doesn't say.
 */
export const inviteSourceFact = (
  invitee: BalanceSlotInvitee | null | undefined
): string | null => {
  const season = invitee?.from?.schedule_name
  return season ? balanceText('common:balanceInviteFactFrom', { season }) : null
}

/**
 * Jeff, Oct 2 — an invited row's own facts, first in its STATUS tooltip: when the
 * invite went out and to whom. They used to sit under the invitee's name, squeezed
 * and cut off; the tooltip has room for all of it and keeps the general meaning
 * below. Null when there is no address to name.
 */
export const inviteSentFact = (
  invitee: BalanceSlotInvitee | null | undefined
): { text: string; email: string } | null => {
  if (!invitee?.email) {
    return null
  }
  const { email } = invitee
  const date = formatInViewerZone(invitee.invited_at, 'MMM D, YYYY')

  return {
    text: date
      ? balanceText('common:balanceInviteFactSent', { date, email })
      : balanceText('common:balanceInviteFactUndated', { email }),
    email,
  }
}
